import { normalizePalette, type ProfilePalette, type PublicProfileCard } from './profile-link-card';
import { shortRelativeTime } from './contact-rows';
import type { OutreachStage } from './outreach-stage';
import { profileDisplayUrl } from './profile-link-view';
import {
  PROFILE_LEAD_STATUSES,
  PROFILE_LINK_ACTIONS,
  PROFILE_LINK_LIMITS,
  type ProfileCardPrefill,
  type ProfileLead,
  type ProfileLeadStatus,
  type ProfileLink,
  type ProfileLinkAction,
  type ProfileLinkPatch,
  type ProfileLinksList,
  type ProfileLinkStats,
  type ProfileLinkStatus,
} from './profile-links-types';

/**
 * The pure decisions behind the owner screens (`/profile-links`, `/profile-links/<id>`): how the list is
 * grouped and ordered, what each status says, what the editor's draft is, which edits become a patch, what a
 * visitor would see, and the share / leads lines. No React, no fetch — every rule is unit-tested
 * (test/profile-links-model.test.ts), and iOS/Android implement the same rules under the same names.
 *
 * docs/symphony-profile-links-design.md §7.1 (the entity), §8.2 (screens), §8.3 (leads), Q2 (the cap).
 */

// ---- the list ----

export interface ProfileLinksSections {
  /** The main profile — the one link with no context. Absent until the account has one. */
  main?: ProfileLink;
  /** Live first, then paused, then drafts; within a status, by title. Archived links are not listed. */
  contexts: ProfileLink[];
}

const STATUS_ORDER: Record<ProfileLinkStatus, number> = { ACTIVE: 0, PAUSED: 1, DRAFT: 2, ARCHIVED: 3 };

export function profileLinksSections(links: readonly ProfileLink[]): ProfileLinksSections {
  const main = links.find((link) => !link.contextSlug && link.status !== 'ARCHIVED');
  const contexts = links
    .filter((link) => link.contextSlug && link.status !== 'ARCHIVED')
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || linkTitleSortKey(a).localeCompare(linkTitleSortKey(b)),
    );
  return { main, contexts };
}

const linkTitleSortKey = (link: ProfileLink) => (link.contextTitle || link.contextSlug || '').toLowerCase();

/** The status pill: its i18n key and the DS badge it wears. */
export function statusPill(status: ProfileLinkStatus): { key: string; variant: 'active' | 'paused' | 'waiting-on-you' | 'done' } {
  switch (status) {
    case 'ACTIVE':
      return { key: 'profileLinks.status.live', variant: 'active' };
    case 'PAUSED':
      return { key: 'profileLinks.status.paused', variant: 'paused' };
    case 'DRAFT':
      return { key: 'profileLinks.status.draft', variant: 'waiting-on-you' };
    default:
      return { key: 'profileLinks.status.archived', variant: 'done' };
  }
}

/**
 * Whether "Fill from what Symphony knows" is offered: some public card field is still empty and nothing is
 * being edited. Symphony only ever fills EMPTY fields, so a card the owner finished has nothing to offer.
 */
export function canPrefillCard(link: ProfileLink, draft: EditorDraft, dirty: boolean): boolean {
  if (dirty || link.status === 'ARCHIVED') return false;
  return [draft.headline, draft.bio, draft.topics, draft.greeting, draft.suggestedAsks].some((value) => !value.trim());
}

/** A message the editor shows: an i18n key and its params. */
export interface EditorNote {
  key: string;
  params?: Record<string, string | number>;
}

/**
 * The message after a fill; `null` when there is nothing to say. An AUTOMATIC run speaks only when it drafted
 * something: the owner did not ask, so "not enough to go on" or a failure would be noise on an editor they
 * opened to type in.
 */
export function prefillNote(prefill: ProfileCardPrefill, auto = false): EditorNote | null {
  if (prefill.outcome === 'filled') return { key: 'profileLinks.prefill.drafted', params: { count: Math.max(1, prefill.filledFields.length) } };
  if (auto) return null;
  if (prefill.outcome === 'nothing_known' || prefill.outcome === 'insufficient_info') return { key: 'profileLinks.prefill.nothingKnown' };
  if (prefill.outcome === 'unavailable') return { key: 'profileLinks.error.saveFailed' };
  return null;
}

/**
 * The ⋯ menu's lifecycle entry: pause a live link, resume a paused one. A link that is not live yet has none —
 * there is no publish step a user sees; the editor's Save makes it live.
 */
export function lifecycleAction(status: ProfileLinkStatus): 'pause' | 'resume' | null {
  if (status === 'ACTIVE') return 'pause';
  if (status === 'PAUSED') return 'resume';
  return null;
}

/** The printed address of a link (`symphony.wix.app/me/<h>[/<c>]`). */
export function linkAddress(link: Pick<ProfileLink, 'handle' | 'contextSlug'>): string {
  return profileDisplayUrl(link.handle, link.contextSlug);
}

/**
 * The views the Settings row counts: the TOTAL across the account's links (`totalViews` on the list), not the
 * main link's own — a context link scanned at an event is the owner's reach too. Anything but a finite
 * non-negative number reads as 0. Same rule on iOS and Android.
 */
export function totalViewsOf(list: Pick<ProfileLinksList, 'totalViews'> | null | undefined): number {
  const value = list?.totalViews;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

const countOf = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);

/**
 * The people who reached the owner across EVERY link the list holds — archived ones included, the same set
 * `totalViews` sums on the server — so the two totals the owner reads side by side count the same links. Each
 * link's `contacts` is one per visitor of that link. Same rule on iOS and Android (`ProfileLinksLogic.totalContacts`).
 */
export function totalContactsOf(links: readonly Pick<ProfileLink, 'stats'>[] | null | undefined): number {
  return (links ?? []).reduce((sum, link) => sum + countOf(link.stats?.contacts), 0);
}

/**
 * The Settings entry row's hint: the main link's address, then the totals across all the account's links — views,
 * and the people who reached the owner. A zero is left out ("Views: 0" on a link nobody has opened yet reads as
 * broken), and with neither it is the address alone. Same rule on iOS and Android (`ProfileLinksLogic.settingsSubtitle`).
 */
export function settingsHint(
  entry: { address: string | null; views: number | null; contacts?: number | null; draft?: boolean },
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  if (!entry.address) return t('profileLinks.settings.setup');
  // A main link that exists but is not live yet: the row says so and its tap opens the editor (same on both phones).
  if (entry.draft) return t('profileLinks.settings.draft');
  const views = countOf(entry.views);
  const contacts = countOf(entry.contacts);
  const parts = [entry.address];
  if (views > 0) parts.push(t('profileLinks.header.views', { count: views }));
  if (contacts > 0) parts.push(t('profileLinks.header.reached', { count: contacts }));
  return parts.join(' · ');
}

/** One number of the Stats section: its copy key, and the value. */
export interface StatRow {
  key: string;
  value: number;
}

/**
 * The Stats section's "All your links" numbers: views and people who reached you across every link (the Settings
 * row's two totals), and how many links are live now. Same rows, same order on iOS and Android
 * (`ProfileLinksLogic.overviewRows`).
 */
export function overviewRows(list: { links: readonly ProfileLink[]; totalViews?: number }): StatRow[] {
  return [
    { key: 'profileLinks.stats.views', value: totalViewsOf(list) },
    { key: 'profileLinks.stats.reached', value: totalContactsOf(list.links) },
    { key: 'profileLinks.stats.liveLinks', value: list.links.filter((link) => link.status === 'ACTIVE').length },
  ];
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** The days "last 7 days" spans, today included — the service's `views_last_7_days` window. */
export const STATS_WINDOW_DAYS = 7;

/**
 * One link's breakdown on the Stats section — its funnel, each step labelled apart so the numbers mean something
 * (Ronny's wix-3-0 on 10-22: 77 views → 42 opened a chat → 15 reached him → 12 actually wrote):
 * - views: lifetime, then today and the last 7 days — those two only when the service sent them (a deploy from
 *   before the day counters sends none, and "Today: 0" over a link viewed today would be wrong);
 * - opened a chat (`openedChats`, only when sent): visitors whose thread opened, wrote or not;
 * - people who reached you (`contacts`, one per visitor: a WhatsApp open, or an app visitor who wrote);
 * - once the leads have loaded: who wrote to you (a lead whose owner-side thread holds a message from them), who
 *   needs you (an open decision on their thread), and who wrote today / in the last 7 days (by the lead's `at`, UTC
 *   days as the views are) — what the People list shows, so the two never disagree.
 * Same rows, same order on iOS and Android (`ProfileLinksLogic.linkStatRows`).
 */
export function linkStatRows(stats: ProfileLinkStats, leads: readonly Pick<ProfileLead, 'at' | 'summary' | 'status'>[] | undefined, now: Date = new Date()): StatRow[] {
  const rows: StatRow[] = [{ key: 'profileLinks.stats.views', value: countOf(stats.views) }];
  if (typeof stats.viewsToday === 'number') rows.push({ key: 'profileLinks.stats.viewsToday', value: countOf(stats.viewsToday) });
  if (typeof stats.viewsLast7Days === 'number') rows.push({ key: 'profileLinks.stats.viewsLast7Days', value: countOf(stats.viewsLast7Days) });
  if (typeof stats.openedChats === 'number') rows.push({ key: 'profileLinks.stats.openedChats', value: countOf(stats.openedChats) });
  rows.push({ key: 'profileLinks.stats.reached', value: countOf(stats.contacts) });
  if (leads) {
    const wrote = leads.filter((lead) => (lead.summary ?? '').trim() !== '');
    const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const windowStart = midnight - (STATS_WINDOW_DAYS - 1) * DAY_MS;
    const times = wrote.map((lead) => Date.parse(lead.at)).filter((ms) => Number.isFinite(ms));
    rows.push({ key: 'profileLinks.stats.wrote', value: wrote.length });
    rows.push({ key: 'profileLinks.stats.needYou', value: leads.filter((lead) => leadStatus(lead) === 'needs_you').length });
    rows.push({ key: 'profileLinks.stats.newToday', value: times.filter((ms) => ms >= midnight).length });
    rows.push({ key: 'profileLinks.stats.newLast7Days', value: times.filter((ms) => ms >= windowStart).length });
  }
  return rows;
}

/** "People" is contacts; "taps" folds the two CTAs together (the mock's four tiles). */
export function statTiles(stats: ProfileLinkStats): { key: string; value: number }[] {
  return [
    { key: 'profileLinks.stats.views', value: stats.views },
    { key: 'profileLinks.stats.taps', value: stats.appTaps + stats.waTaps },
    { key: 'profileLinks.stats.people', value: stats.contacts },
    { key: 'profileLinks.stats.joined', value: stats.signups },
  ];
}

// ---- the editor ----

export const EDITOR_TABS = ['card', 'ai', 'design', 'settings', 'people'] as const;
export type EditorTab = (typeof EDITOR_TABS)[number];

/** `?tab=` → a tab; anything else is the Card tab. */
export function parseEditorTab(value: unknown): EditorTab {
  return typeof value === 'string' && (EDITOR_TABS as readonly string[]).includes(value) ? (value as EditorTab) : 'card';
}

/**
 * The editor's working copy. Lists are edited as one line per item, which is what the owner types; the
 * palette is the accent only (D8: the page is one fixed Symphony template, the owner picks its colour).
 */
export interface EditorDraft {
  contextTitle: string;
  headline: string;
  bio: string;
  topics: string;
  greeting: string;
  suggestedAsks: string;
  instructions: string;
  allowedActions: ProfileLinkAction[];
  accent: string;
  /** `YYYY-MM-DD`, or '' for never. Always '' on the main profile (D7). */
  expiresOn: string;
  /** Credits per day as typed; '' or '0' = no cap. */
  dailyCreditCap: string;
}

/* EXEMPT — the swatches are DATA an owner stores (their page's accent), written into the public page as
   custom properties, not app styling. The first is the Symphony brand green, --ds-fill-accent-brand-default. */
export const PALETTE_SWATCHES: readonly string[] = ['#a4ff70', '#8fb8ff', '#ffb199', '#c9b6ff'];
/* end EXEMPT */

/** One item per line; blank lines dropped; each trimmed. Duplicates are kept — the owner may mean them. */
export function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

const joinLines = (items: readonly string[]) => items.join('\n');

export function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : new Date(ms).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` → the END of that day in UTC, so a link "expires on Nov 30" still works on Nov 30. */
export function dateInputToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const ms = Date.parse(`${value}T23:59:59.000Z`);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export function draftFromLink(link: ProfileLink): EditorDraft {
  return {
    contextTitle: link.contextTitle ?? '',
    headline: link.card.headline,
    bio: link.card.bio,
    topics: joinLines(link.card.topics),
    greeting: link.card.greeting,
    suggestedAsks: joinLines(link.card.suggestedAsks),
    instructions: link.instructions,
    allowedActions: normalizeActions(link.allowedActions),
    accent: normalizePalette(link.card.design.palette).accent,
    expiresOn: link.contextSlug ? isoToDateInput(link.expiresAt) : '',
    dailyCreditCap: link.stats.dailyCreditCap > 0 ? String(link.stats.dailyCreditCap) : '',
  };
}

export type DraftErrors = Partial<Record<keyof EditorDraft, { key: string; params?: Record<string, number> }>>;

/** What blocks a save. Keys are i18n keys, so the editor renders them in the reader's language. */
export function validateDraft(draft: EditorDraft, link: Pick<ProfileLink, 'contextSlug'>): DraftErrors {
  const L = PROFILE_LINK_LIMITS;
  const errors: DraftErrors = {};
  const tooLong = (field: keyof EditorDraft, value: string, max: number) => {
    if (value.length > max) errors[field] = { key: 'profileLinks.error.tooLong', params: { max } };
  };
  if (link.contextSlug) {
    if (!draft.contextTitle.trim()) errors.contextTitle = { key: 'profileLinks.error.required' };
    else tooLong('contextTitle', draft.contextTitle, L.contextTitle);
  }
  tooLong('headline', draft.headline, L.headline);
  tooLong('bio', draft.bio, L.bio);
  tooLong('greeting', draft.greeting, L.greeting);
  tooLong('instructions', draft.instructions, L.instructions);
  const topics = splitLines(draft.topics);
  if (topics.length > L.topics) errors.topics = { key: 'profileLinks.error.tooMany', params: { max: L.topics } };
  else if (topics.some((t) => t.length > L.topic)) errors.topics = { key: 'profileLinks.error.itemTooLong', params: { max: L.topic } };
  const asks = splitLines(draft.suggestedAsks);
  if (asks.length > L.suggestedAsks) errors.suggestedAsks = { key: 'profileLinks.error.tooMany', params: { max: L.suggestedAsks } };
  else if (asks.some((a) => a.length > L.suggestedAsk)) errors.suggestedAsks = { key: 'profileLinks.error.itemTooLong', params: { max: L.suggestedAsk } };
  if (draft.expiresOn && !dateInputToIso(draft.expiresOn)) errors.expiresOn = { key: 'profileLinks.error.invalidDate' };
  if (draft.dailyCreditCap.trim()) {
    const cap = Number(draft.dailyCreditCap);
    if (!Number.isInteger(cap) || cap < 0 || cap > L.dailyCreditCap) errors.dailyCreditCap = { key: 'profileLinks.error.invalidCap' };
  }
  return errors;
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const sameActions = (a: readonly ProfileLinkAction[], b: readonly ProfileLinkAction[]) =>
  sameList([...a].sort(), [...b].sort());

/**
 * ONLY what changed, or null when nothing did. A write that restates unchanged values clobbers a concurrent
 * edit by the Maestro (`ManageProfileLinks update`, §8.1) — the same reason `routine-update.ts` sends a diff.
 */
export function patchFromDraft(link: ProfileLink, draft: EditorDraft): ProfileLinkPatch | null {
  const patch: ProfileLinkPatch = {};
  const card: NonNullable<ProfileLinkPatch['card']> = {};
  if (link.contextSlug && draft.contextTitle.trim() !== (link.contextTitle ?? '')) patch.contextTitle = draft.contextTitle.trim();
  if (draft.headline.trim() !== link.card.headline) card.headline = draft.headline.trim();
  if (draft.bio.trim() !== link.card.bio) card.bio = draft.bio.trim();
  if (draft.greeting.trim() !== link.card.greeting) card.greeting = draft.greeting.trim();
  const topics = splitLines(draft.topics);
  if (!sameList(topics, link.card.topics)) card.topics = topics;
  const asks = splitLines(draft.suggestedAsks);
  if (!sameList(asks, link.card.suggestedAsks)) card.suggestedAsks = asks;
  const palette = normalizePalette(link.card.design.palette);
  const accent = draft.accent.toLowerCase();
  if (accent !== palette.accent) card.design = { palette: normalizePalette({ ...palette, accent }) };
  if (Object.keys(card).length) patch.card = card;
  if (draft.instructions !== link.instructions) patch.instructions = draft.instructions;
  // MESSAGE is always on (answering IS the link). A link stored without it is not an edit the owner made, so it is
  // compared normalized: opening the editor on it is not dirty, and any action change writes MESSAGE back.
  const actions = normalizeActions(draft.allowedActions);
  if (!sameActions(actions, normalizeActions(link.allowedActions))) patch.allowedActions = actions;
  if (link.contextSlug) {
    const expiresAt = draft.expiresOn ? dateInputToIso(draft.expiresOn) : null;
    if (isoToDateInput(expiresAt ?? undefined) !== isoToDateInput(link.expiresAt)) patch.expiresAt = expiresAt;
  }
  const cap = draft.dailyCreditCap.trim() ? Number(draft.dailyCreditCap) : 0;
  if (Number.isInteger(cap) && cap !== link.stats.dailyCreditCap) patch.dailyCreditCap = cap;
  return Object.keys(patch).length ? patch : null;
}

/**
 * MESSAGE first, then the rest in canonical order, deduped. MESSAGE is not a choice: a link exists so the owner's AI
 * answers the people who write (the service opens every link on it, VMR #32542), and a link without it refused every
 * reply. Same rule on iOS and Android (`normalizeActions`).
 */
export function normalizeActions(actions: readonly ProfileLinkAction[]): ProfileLinkAction[] {
  const set = new Set<ProfileLinkAction>(actions);
  set.add('MESSAGE');
  return PROFILE_LINK_ACTIONS.filter((a) => set.has(a));
}

export function toggleAction(actions: readonly ProfileLinkAction[], action: ProfileLinkAction, on: boolean): ProfileLinkAction[] {
  const set = new Set(actions);
  if (on) set.add(action);
  else if (action !== 'MESSAGE') set.delete(action);
  return normalizeActions([...set]);
}

/** The "Your AI can" rows, in order, with their i18n keys. MESSAGE has none: it is always on. */
export const ACTION_ROWS: readonly { action: ProfileLinkAction; key: string }[] = [
  { action: 'REQUEST_FILE', key: 'profileLinks.ai.action.files' },
  { action: 'REQUEST_MEETING', key: 'profileLinks.ai.action.meetings' },
];

/**
 * What a visitor would see right now — the live preview beside the editor, rendered by the same
 * `ProfileLanding` the public page uses, so the preview cannot drift from the page.
 */
export function previewCard(link: ProfileLink, draft: EditorDraft, displayName: string): PublicProfileCard {
  const palette: ProfilePalette = normalizePalette({ ...link.card.design.palette, accent: draft.accent });
  return {
    handle: link.handle,
    contextSlug: link.contextSlug,
    contextTitle: link.contextSlug ? draft.contextTitle.trim() || link.contextSlug : undefined,
    displayName: displayName || link.handle,
    headline: draft.headline.trim(),
    bio: draft.bio.trim(),
    topics: splitLines(draft.topics),
    ...(draft.greeting.trim() ? { greeting: draft.greeting.trim() } : {}),
    ...(splitLines(draft.suggestedAsks).length ? { suggestedAsks: splitLines(draft.suggestedAsks) } : {}),
    palette,
    // A preview's buttons go nowhere real: the short code is the backend's, and the page is not live yet.
    waShortCode: 'SY-PREVIEW',
    appLink: '',
    fellBackToMain: false,
  };
}

// ---- settings: the daily credit cap (Q2) ----

export interface CreditsLine {
  used: number;
  /** 0 = no cap. */
  cap: number;
  /** At or past the cap: the AI stops answering, messages still arrive (§11.2 B). */
  capped: boolean;
}

export function creditsLine(stats: ProfileLinkStats): CreditsLine {
  const cap = Math.max(0, stats.dailyCreditCap);
  return { used: stats.creditsToday, cap, capped: cap > 0 && stats.creditsToday >= cap };
}

// ---- leads ----

export function leadInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * What the People row says about a lead: the bridge's `status` (read from the lead's outreach and open decisions —
 * an open decision for the owner, a reply reached the visitor, or neither), case-insensitively. A missing or unknown
 * status reads `new`, never `needs_you`: the service's `state` is WAITING_FOR_YOU from the first tap, so it says
 * nothing about whether the owner is needed. Same rule on iOS and Android.
 */
export function leadStatus(lead: Pick<ProfileLead, 'status'>): ProfileLeadStatus {
  const status = typeof lead.status === 'string' ? (lead.status.toLowerCase() as ProfileLeadStatus) : undefined;
  return status && PROFILE_LEAD_STATUSES.includes(status) ? status : 'new';
}

export function leadStatusKey(status: ProfileLeadStatus): string {
  if (status === 'needs_you') return 'profileLinks.people.needsYou';
  return status === 'new' ? 'profileLinks.people.new' : 'profileLinks.people.aiAnswered';
}

/** Needs-you first (the ones that need the owner), then newest first. */
export function sortLeads(leads: readonly ProfileLead[]): ProfileLead[] {
  return [...leads].sort((a, b) => {
    const waiting = Number(leadStatus(b) === 'needs_you') - Number(leadStatus(a) === 'needs_you');
    return waiting || Date.parse(b.at) - Date.parse(a.at);
  });
}

/** The header's avatar stack: the newest `max` people who wrote, and how many more there are ("+N"). */
export function headerAvatars(leads: readonly ProfileLead[], max = 4): { shown: ProfileLead[]; more: number } {
  const newest = [...leads].sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));
  const shown = newest.slice(0, Math.max(0, max));
  return { shown, more: newest.length - shown.length };
}

export const leadChannelKey = (lead: Pick<ProfileLead, 'channel'>) =>
  lead.channel === 'WHATSAPP' ? 'profileLinks.people.channel.whatsapp' : 'profileLinks.people.channel.app';

// ---- the share panel ----

export const SHARE_PRESETS = ['symphony', 'owner', 'projector'] as const;
export type SharePreset = (typeof SHARE_PRESETS)[number];

export const SHARE_PRESET_KEYS: Record<SharePreset, string> = {
  symphony: 'profileLinks.share.preset.symphony',
  owner: 'profileLinks.share.preset.owner',
  projector: 'profileLinks.share.preset.plain',
};

/** A filename the owner will recognise in their Downloads: `symphony-ronny.elkayam-devconf26.svg`. */
export function qrFileName(link: Pick<ProfileLink, 'handle' | 'contextSlug'>, ext: 'svg' | 'png'): string {
  const slug = [link.handle, link.contextSlug].filter(Boolean).join('-').replace(/[^a-z0-9.-]/gi, '-');
  return `symphony-${slug}.${ext}`;
}

// ---- new context link ----

/** A title the owner typed → a URL slug (`DevConf 26` → `devconf-26`). Empty when nothing usable is left. */
export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

// ── Lead cards in People (§8.3) ──

/** Links whose leads People reads, at most — People is a summary; the link's own People tab is the full list. */
export const PEOPLE_LEAD_LINKS_MAX = 10;
/** Lead rows People shows at most. */
export const PEOPLE_LEADS_MAX = 20;

/** The links whose leads People reads: every link a visitor could ever have reached (a draft never could). */
export function peopleLeadLinks(links: readonly ProfileLink[]): ProfileLink[] {
  return links.filter((link) => link.status !== 'DRAFT').slice(0, PEOPLE_LEAD_LINKS_MAX);
}

/**
 * One lead as People shows it, with the link it came through: `linkPath` (`/me/<handle>[/<context>]`) and
 * `linkLabel` (its context title, else its slug; null = the main profile). The row reads
 * "<path> · <label or Main profile>", as iOS and Android `ProfileLinksLogic.leadSource` do.
 */
export interface PeopleLeadRow {
  key: string;
  lead: ProfileLead;
  linkId: string;
  linkPath: string;
  linkLabel: string | null;
}

/**
 * Every lead of every read link, as People's rows: newest first contact first, at most
 * {@link PEOPLE_LEADS_MAX}. A link whose leads are not loaded (yet, or at all) contributes nothing — the
 * section never waits on its slowest link.
 */
export function peopleLeadRows(
  links: readonly ProfileLink[],
  leadsByLink: Readonly<Record<string, readonly ProfileLead[] | undefined>>,
): PeopleLeadRow[] {
  const rows: PeopleLeadRow[] = [];
  for (const link of links) {
    for (const lead of leadsByLink[link.id] ?? []) {
      const slug = link.contextSlug?.trim();
      rows.push({
        key: `${link.id}:${lead.id}`,
        lead,
        linkId: link.id,
        linkPath: `/me/${link.handle}${slug ? `/${slug}` : ''}`,
        linkLabel: slug ? link.contextTitle?.trim() || slug : null,
      });
    }
  }
  const at = (row: PeopleLeadRow) => Date.parse(row.lead.at) || 0;
  return rows.sort((a, b) => at(b) - at(a)).slice(0, PEOPLE_LEADS_MAX);
}

/** Where tapping a lead goes: its owner-side conversation when there is one, else the link's People tab. */
export type LeadOpenTarget = { kind: 'conversation'; conversationId: string } | { kind: 'link'; linkId: string };

export function leadOpenTarget(row: Pick<PeopleLeadRow, 'lead' | 'linkId'>): LeadOpenTarget {
  const conversationId = row.lead.conversationId?.trim();
  return conversationId ? { kind: 'conversation', conversationId } : { kind: 'link', linkId: row.linkId };
}

// ── A lead who is already a contact (§8.3) ──
//
// A profile-link lead IS an outreach on the owner's site (owner → visitor), and once the visitor writes, People
// resolves them onto the owner's Person — so the contact book already lists that person, often with older threads
// of their own. Without this, the same human was a lead card above the list AND a contact in it. The lead is
// matched to the contact and folded into its row as a "Wrote via /me/<handle>" note; only leads with no contact
// stay in "From your profile links". 1:1 with iOS/Android `ProfileLinksLogic` under the same names.

/** Fewer digits than this is not a phone number to match a person by. */
const LEAD_PHONE_MIN_DIGITS = 7;

/**
 * A phone as a match key: its digits only, '' when there are too few to mean anything. Deliberately no country
 * rules — the repo has no country-aware normalizer (`lib/recipient-field.ts` reduces phones to digits the same
 * way), so `+972545550134` and `0545550134` do NOT match: a false match would hide a real lead.
 */
export function leadPhoneKey(value: string | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length >= LEAD_PHONE_MIN_DIGITS ? digits : '';
}

/**
 * A link path inside translated copy, isolated left-to-right (LRI U+2066 … PDI U+2069): in a right-to-left
 * sentence (he/ar) a bare `/me/ronny` reorders to `me/ronny/`. Only the shown text — the raw `linkPath` stays
 * bare. Same isolate as `alfred-settings.ts` `ltrIsolate` and iOS `PersonCardSections.ltr`.
 */
export function leadLinkIsolate(path: string): string {
  return `\u2066${path}\u2069`;
}

/**
 * A lead card's provenance line — `<path> · <label or Main profile>` — with the path isolated
 * ({@link leadLinkIsolate}). Mirrors iOS/Android `ProfileLinksLogic.leadSource`.
 */
export function leadSource(row: Pick<PeopleLeadRow, 'linkPath' | 'linkLabel'>, mainLabel: string): string {
  return [row.linkPath ? leadLinkIsolate(row.linkPath) : '', row.linkLabel ?? mainLabel].filter(Boolean).join(' · ');
}

// ── Who a lead is and whether they need the owner (Outreach screen, issue 8 of 2026-10-22) ──
//
// A visitor whose account carries no display name used to read "Unnamed contact" — indistinguishable from every
// other one. The link they came through is the one thing the owner knows about them, so it names them. Mirrors
// iOS/Android `ProfileLinksLogic` under the same names.

/** The name a lead shows: the visitor's own, else "Visitor from <context title | Main profile>". */
export function leadDisplayName(
  row: Pick<PeopleLeadRow, 'lead' | 'linkLabel'>,
  visitorFrom: (link: string) => string,
  mainLabel: string,
): string {
  return row.lead.name?.trim() || visitorFrom(row.linkLabel ?? mainLabel);
}

/**
 * What a lead row says about the owner: "Needs you" only with an open decision for them (`leadStatus` needs_you);
 * every other lead — answered, or not answered yet with nothing raised — is the AI's ("Your AI is handling it").
 */
export function leadRowStatus(lead: Pick<ProfileLead, 'status'>): { key: string; needsYou: boolean } {
  return leadStatus(lead) === 'needs_you'
    ? { key: 'profileLinks.people.needsYou', needsYou: true }
    : { key: 'profileLinks.leads.aiHandling', needsYou: false };
}

/** The section's order: the leads that need the owner first, then newest first. Stable; never mutates. */
export function sortLeadRowsForSection<R extends Pick<PeopleLeadRow, 'lead'>>(rows: readonly R[]): R[] {
  const at = (row: R) => Date.parse(row.lead.at) || 0;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const needs = Number(leadRowStatus(b.row.lead).needsYou) - Number(leadRowStatus(a.row.lead).needsYou);
      return needs || at(b.row) - at(a.row) || a.i - b.i;
    })
    .map(({ row }) => row);
}

/** By outreach id, the name each lead shows ({@link leadDisplayName}) — for a contact-book row that names nobody. */
export function leadNamesByOutreach(
  rows: readonly Pick<PeopleLeadRow, 'lead' | 'linkLabel'>[],
  visitorFrom: (link: string) => string,
  mainLabel: string,
): Record<string, string> {
  const names: Record<string, string> = {};
  for (const row of rows) {
    const id = row.lead.outreachId?.trim();
    if (id && !names[id]) names[id] = leadDisplayName(row, visitorFrom, mainLabel);
  }
  return names;
}

/** The "unnamed" fallback a row passes to its name rule: its lead's name when one of its outreaches is a lead's. */
export function unnamedLabelFor(outreachIds: readonly (string | undefined)[], names: Readonly<Record<string, string>>, unnamed: string): string {
  for (const id of outreachIds) {
    const name = id ? names[id.trim()] : undefined;
    if (name) return name;
  }
  return unnamed;
}

/** What a contact can be matched to a lead by. `key` is the row's identity ('' = unmatchable). */
export interface LeadMatchContact {
  key: string;
  outreachIds: ReadonlySet<string>;
  phones: ReadonlySet<string>;
}

/** The subset of a contact-book entry the match reads. */
export interface LeadMatchEntry {
  contact?: { personId?: string };
  recipientId?: string;
  outbound?: readonly { outreachId?: string }[];
  inbound?: readonly { outreachId?: string }[];
  channels?: readonly { kind?: string; value?: string }[];
}

/**
 * The identity half of {@link contactLeadKeys} — person id, else recipient id, else its first outreach id — cheap
 * enough for a row to look its note up by while it renders (no sets built). Mirrors iOS/Android `contactLeadKey`.
 */
export function contactLeadKey(entry: LeadMatchEntry): string {
  const firstId = (threads: readonly { outreachId?: string }[] | undefined) =>
    threads?.map((t) => t.outreachId?.trim() ?? '').find(Boolean);
  return entry.contact?.personId?.trim() || entry.recipientId?.trim() || firstId(entry.outbound) || firstId(entry.inbound) || '';
}

/**
 * A contact's match keys: every outreach id it lists, and its phone channels as {@link leadPhoneKey}s. No email:
 * a lead carries none. Never the name — a lead's name is self-asserted.
 */
export function contactLeadKeys(entry: LeadMatchEntry): LeadMatchContact {
  const ids = [...(entry.outbound ?? []), ...(entry.inbound ?? [])].map((t) => t.outreachId?.trim() ?? '').filter(Boolean);
  const phones = (entry.channels ?? [])
    .filter((c) => (c.kind ?? '').trim().toLowerCase() === 'phone')
    .map((c) => leadPhoneKey(c.value))
    .filter(Boolean);
  return {
    key: contactLeadKey(entry),
    outreachIds: new Set(ids),
    phones: new Set(phones),
  };
}

export interface LeadContactMatch {
  /** Leads with no contact — what "From your profile links" still shows, in the order given. */
  unmatched: PeopleLeadRow[];
  /** A contact's newest lead, by {@link LeadMatchContact.key}. An older lead of the same contact is dropped. */
  byContact: Record<string, PeopleLeadRow>;
}

/**
 * Splits People's lead rows (newest first) into those a loaded contact already is and the rest. A lead matches the
 * first contact listing its outreach id — the stable key — else the ONE contact with its phone. A phone more than
 * one loaded contact holds is ambiguous: no phone match, and the lead stays in the section rather than being
 * folded into a guess. Pure.
 */
export function matchLeadsToContacts(rows: readonly PeopleLeadRow[], contacts: readonly LeadMatchContact[]): LeadContactMatch {
  const candidates = contacts.filter((c) => c.key);
  const phoneHolders = new Map<string, number>();
  for (const c of candidates) for (const p of c.phones) phoneHolders.set(p, (phoneHolders.get(p) ?? 0) + 1);
  const unmatched: PeopleLeadRow[] = [];
  const byContact: Record<string, PeopleLeadRow> = {};
  for (const row of rows) {
    const outreachId = row.lead.outreachId?.trim();
    const phone = leadPhoneKey(row.lead.phone);
    const contact =
      (outreachId ? candidates.find((c) => c.outreachIds.has(outreachId)) : undefined) ??
      (phone && phoneHolders.get(phone) === 1 ? candidates.find((c) => c.phones.has(phone)) : undefined);
    if (!contact) unmatched.push(row);
    else if (!byContact[contact.key]) byContact[contact.key] = row;
  }
  return { unmatched, byContact };
}

/**
 * Whether "From your profile links" draws. Hidden only while the contacts list's FIRST read is pending — drawn
 * before it settles, every lead who is a contact would sit in the section and then jump into the list. Once it
 * settles it shows whether the read succeeded or FAILED: the leads are their own read and never wait on the
 * contacts; after a failure there are no contacts to match, so every lead is unmatched. Never under a search (the
 * search reaches the people book; leads are not in it). Mirrors iOS/Android `ProfileLinksLogic.leadsSectionShown`.
 */
export function leadsSectionShown(firstContactsReadSettled: boolean, searching: boolean): boolean {
  return firstContactsReadSettled && !searching;
}

/**
 * The note a matched contact's row wears in place of its subtitle — "Wrote via /me/ronny · 1d" — or null when the
 * row's own line matters more: the owner must act (`needsYou`) or a send failed (`stuck`). Provenance never hides
 * either.
 */
export function contactLeadNote(
  row: Pick<PeopleLeadRow, 'lead' | 'linkPath'>,
  stage: OutreachStage | null,
  now: Date = new Date(),
  /** `t('contacts.time.now')` — under a minute reads as the row's own "Now", as the contact rows do. */
  nowLabel?: string,
): { key: 'contacts.row.wroteVia'; params: { link: string; time: string } } | null {
  if (stage === 'needsYou' || stage === 'stuck') return null;
  // The link is isolated LTR (`leadLinkIsolate`): in he/ar the bare path read `me/ronny/`.
  return {
    key: 'contacts.row.wroteVia',
    params: { link: leadLinkIsolate(row.linkPath), time: shortRelativeTime(row.lead.at, now, nowLabel) },
  };
}

// ── The A2 owner screen: one link at a time, its formats, and Edit in a dialog ──

/** The Edit dialog's sections — Card, Your AI, Look (`design`), Settings. People moved out of the editor. */
export const EDIT_TABS = ['card', 'ai', 'design', 'settings'] as const;
export type EditTab = (typeof EDIT_TABS)[number];

/** `?tab=` → the section Edit opens on. `people` is not a section any more; it opens the People dialog. */
export function parseEditTab(value: unknown): EditTab {
  const tab = parseEditorTab(value);
  return tab === 'people' ? 'card' : tab;
}

/**
 * The Edit dialog's footer: ONE Save. Save writes the edit and, unless the link is paused, makes it live (the
 * screen chains publish). Offered when there is an edit, or on a link that is not live yet (a legacy draft, or a
 * go-live the service refused) so the owner can try again after fixing it. Same rule on iOS and Android.
 */
export function editDialogActions(link: ProfileLink, dirty: boolean, hasErrors: boolean): { save: boolean } {
  if (hasErrors || link.status === 'ARCHIVED') return { save: false };
  return { save: dirty || link.status === 'DRAFT' };
}

/** Whether Save also makes the link live: everything but a paused link (publishing a paused link RESUMES it). */
export function saveGoesLive(link: Pick<ProfileLink, 'status'>): boolean {
  return link.status === 'DRAFT' || link.status === 'ACTIVE';
}

export type MoreAction = 'pause' | 'resume' | 'people' | 'archive';

/** The ⋯ menu, in order. Archive is a context link's only: the main profile is paused, never archived. */
export function moreActions(link: ProfileLink): MoreAction[] {
  const actions: MoreAction[] = [];
  const lifecycle = lifecycleAction(link.status);
  if (lifecycle) actions.push(lifecycle);
  actions.push('people');
  if (link.contextSlug && link.status !== 'ARCHIVED') actions.push('archive');
  return actions;
}

/**
 * Which part of the card a focused field draws, so the Edit dialog's preview can mark it. Fields a visitor
 * does not see on the card (the first message, the suggested questions, everything private) mark nothing.
 */
export const PREVIEW_FIELD: Partial<Record<keyof EditorDraft, 'context' | 'headline' | 'bio' | 'topics' | 'band'>> = {
  contextTitle: 'context',
  headline: 'headline',
  bio: 'bio',
  topics: 'topics',
  accent: 'band',
};

/** The switcher's count: shown once there is more than the main profile to switch between. */
export function switcherCount(links: readonly ProfileLink[]): number | null {
  const { main, contexts } = profileLinksSections(links);
  const count = (main ? 1 : 0) + contexts.length;
  return count > 1 ? count : null;
}
